import { useContext, useState } from 'react'
import {
    Card,
    Col,
    DatePicker,
    Row,
    Table,
    Typography,
} from 'antd'
import { Column } from '@ant-design/charts'
import { useQuery } from 'react-query'
import { useTranslation } from 'react-i18next'
import i18next from 'i18next'
import moment from 'moment'

import { AppContext } from '../../context/AppContext'
import { getSupplierAnalytics } from '../../network/Suppliers'

import Loader from '../../components/Loader'
import Error from '../../components/Error'
import StatCard from '../../components/StatCard'
import DynamicStrings from '../../components/DynamicStrings'
import { formatNb } from '../../utils/utils'

const { RangePicker } = DatePicker
const { Title, Text } = Typography

const getProductLabel = (product) => {
    if (!product) return '-'

    if (typeof product.name === 'string') {
        return product.name
    }

    if (product.name?.[i18next.language]) {
        return product.name[i18next.language]
    }

    return product.shortName || '-'
}

const formatChartAmount = (amount) =>
    Number((amount ?? 0).toFixed(2))

const toFilterDates = (range) => {
    if (!range?.[0] || !range?.[1]) {
        return {}
    }

    return {
        dateStart: range[0].clone().startOf('day').toDate(),
        dateEnd: range[1].clone().endOf('day').toDate(),
    }
}

const productColumns = (t) => [
    {
        title: '#',
        dataIndex: 'rank',
        key: 'rank',
        width: 56,
        align: 'center',
    },
    {
        title: t('OFFER_ANALYTICS_ITEM'),
        key: 'name',
        ellipsis: true,
        render: (_, record) => (
            <div className="supplier-analytics-product-cell">
                <Text strong>{getProductLabel(record)}</Text>
                {record.productId ? (
                    <Text type="secondary" className="supplier-analytics-product-code">
                        {record.productId}
                    </Text>
                ) : record.shortName ? (
                    <Text type="secondary" className="supplier-analytics-product-code">
                        {record.shortName}
                    </Text>
                ) : null}
            </div>
        ),
    },
    {
        title: t('OFFER_ANALYTICS_VOLUME'),
        dataIndex: 'unitsSold',
        key: 'unitsSold',
        width: 96,
        align: 'right',
        render: (value) => formatNb(value || 0),
    },
]

const SupplierRevenueChart = ({ title, data, filterExtra, emptyLabel }) => {
    const chartData = data || []

    const config = {
        data: chartData,
        xField: '_id',
        yField: 'amount',
        autoFit: true,
        label: false,
        padding: [24, 16, 48, 48],
        xAxis: {
            label: {
                autoRotate: true,
                autoHide: false,
            },
        },
        yAxis: {
            label: {
                formatter: (value) => formatNb(value),
            },
        },
        tooltip: {
            formatter: (datum) => ({
                name: datum._id,
                value: '¥ ' + formatNb(datum.amount),
            }),
        },
        meta: {
            amount: {
                alias: 'Revenue',
                formatter: (value) => formatNb(value),
            },
        },
    }

    return (
        <Card
            title={title}
            extra={filterExtra}
            className="supplier-analytics-panel supplier-analytics-chart"
        >
            {chartData.length ? (
                <Column {...config} style={{ height: 280 }} />
            ) : (
                <div className="supplier-analytics-chart-empty">{emptyLabel}</div>
            )}
        </Card>
    )
}

const SupplierAnalytics = ({ supplierId }) => {
    const { t } = useTranslation()
    const { token } = useContext(AppContext)
    const [monthlyFilter, setMonthlyFilter] = useState({
        dateStart: moment().startOf('year').toDate(),
        dateEnd: moment().endOf('year').toDate(),
    })
    const [yearlyFilter, setYearlyFilter] = useState({})

    const analytics = useQuery(
        [
            'getSupplierAnalytics',
            {
                supplierId,
                monthlyDateStart: monthlyFilter.dateStart,
                monthlyDateEnd: monthlyFilter.dateEnd,
                yearlyDateStart: yearlyFilter.dateStart,
                yearlyDateEnd: yearlyFilter.dateEnd,
                token: token.token,
            },
        ],
        getSupplierAnalytics
    )

    const data = analytics.data

    const topProducts =
        data?.topProducts ??
        (data?.topProduct ? [data.topProduct] : [])

    const worstProducts =
        data?.worstProducts ??
        (data?.worstProduct ? [data.worstProduct] : [])

    const monthlyChartData = data?.monthlyRevenues?.map((entry) => ({
        _id: entry._id,
        amount: formatChartAmount(entry.amount),
    }))

    const yearlyChartData = data?.yearlyRevenues?.map((entry) => ({
        _id: entry._id,
        amount: formatChartAmount(entry.amount),
    }))

    return analytics.isError ? (
        <Error retry={() => analytics.refetch()} />
    ) : (
        <div className="supplier-analytics">
            {analytics.isLoading ? (
                <Loader />
            ) : (
                <>
                    <section className="supplier-analytics-section">
                        <Title level={4} className="supplier-analytics-section-title">
                            <DynamicStrings id="ANALYTICS_SALES" />
                        </Title>
                        <Row gutter={[16, 16]}>
                            <Col xs={24} sm={12} xl={6}>
                                <StatCard
                                    className="supplier-analytics-stat"
                                    title={
                                        <DynamicStrings id="SUPPLIERS_GLOBAL_REVENUE" />
                                    }
                                    value={
                                        data?.globalRevenue
                                            ? '¥ ' + formatNb(data.globalRevenue)
                                            : '¥ 0'
                                    }
                                />
                            </Col>
                            <Col xs={24} sm={12} xl={6}>
                                <StatCard
                                    className="supplier-analytics-stat"
                                    title={
                                        <DynamicStrings id="SUPPLIERS_CLAIM_RATE" />
                                    }
                                    value={
                                        data?.orderCount
                                            ? formatNb(data.claimRate) + '%'
                                            : '0%'
                                    }
                                    subtitle={`${data?.claimCount || 0} / ${
                                        data?.orderCount || 0
                                    } ${t('MENU_ORDERS').toLowerCase()}`}
                                />
                            </Col>
                        </Row>
                    </section>

                    <section className="supplier-analytics-section">
                        <Title level={4} className="supplier-analytics-section-title">
                            <DynamicStrings id="DIRECT_SALES_PRODUCTS" />
                        </Title>
                        <Row gutter={[16, 16]} align="stretch">
                            <Col xs={24} xl={12}>
                                <Card
                                    title={
                                        <DynamicStrings id="SUPPLIERS_TOP_PRODUCTS" />
                                    }
                                    className="supplier-analytics-panel supplier-analytics-table"
                                >
                                    <Table
                                        pagination={false}
                                        size="middle"
                                rowKey={(record, index) =>
                                    `top-${record.productId || record.shortName || index}-${index}`
                                }
                                        dataSource={topProducts}
                                        columns={productColumns(t)}
                                        locale={{ emptyText: '-' }}
                                    />
                                </Card>
                            </Col>
                            <Col xs={24} xl={12}>
                                <Card
                                    title={
                                        <DynamicStrings id="SUPPLIERS_WORST_PRODUCTS" />
                                    }
                                    className="supplier-analytics-panel supplier-analytics-table"
                                >
                                    <Table
                                        pagination={false}
                                        size="middle"
                                rowKey={(record, index) =>
                                    `worst-${record.productId || record.shortName || index}-${index}`
                                }
                                        dataSource={worstProducts}
                                        columns={productColumns(t)}
                                        locale={{ emptyText: '-' }}
                                    />
                                </Card>
                            </Col>
                        </Row>
                    </section>

                    <section className="supplier-analytics-section">
                        <Title level={4} className="supplier-analytics-section-title">
                            <DynamicStrings id="MENU_ANALYTICS" />
                        </Title>
                        <Row gutter={[16, 16]} align="stretch">
                            <Col xs={24} xl={12}>
                                <SupplierRevenueChart
                                    title={t('SUPPLIERS_MONTHLY_REVENUE')}
                                    data={monthlyChartData}
                                    emptyLabel="-"
                                    filterExtra={
                                        <RangePicker
                                            allowClear={false}
                                            value={[
                                                moment(monthlyFilter.dateStart),
                                                moment(monthlyFilter.dateEnd),
                                            ]}
                                            onChange={(range) =>
                                                setMonthlyFilter(toFilterDates(range))
                                            }
                                        />
                                    }
                                />
                                <Text
                                    type="secondary"
                                    className="supplier-analytics-filter-hint"
                                >
                                    {t('SUPPLIERS_MONTHLY_REVENUE_DATES')}
                                </Text>
                            </Col>
                            <Col xs={24} xl={12}>
                                <SupplierRevenueChart
                                    title={t('SUPPLIERS_YEARLY_REVENUE')}
                                    data={yearlyChartData}
                                    emptyLabel="-"
                                    filterExtra={
                                        <RangePicker
                                            allowClear
                                            placeholder={[
                                                t('ANALYTICS_START_DATE'),
                                                t('ANALYTICS_END_DATE'),
                                            ]}
                                            value={
                                                yearlyFilter.dateStart &&
                                                yearlyFilter.dateEnd
                                                    ? [
                                                          moment(
                                                              yearlyFilter.dateStart
                                                          ),
                                                          moment(
                                                              yearlyFilter.dateEnd
                                                          ),
                                                      ]
                                                    : undefined
                                            }
                                            onChange={(range) =>
                                                setYearlyFilter(toFilterDates(range))
                                            }
                                        />
                                    }
                                />
                                <Text
                                    type="secondary"
                                    className="supplier-analytics-filter-hint"
                                >
                                    {t('SUPPLIERS_YEARLY_REVENUE_DATES')}
                                </Text>
                            </Col>
                        </Row>
                    </section>
                </>
            )}
        </div>
    )
}

export default SupplierAnalytics
